import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, authApi } from "@/lib/api";
import { toast } from "sonner";
import {
  ChevronLeft,
  Gift,
  Star,
  Sparkles,
  UserRound,
  LogOut,
  Phone,
} from "lucide-react";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import { useAuth } from "@/context/AuthContext";
import { T } from "@/context/TextEditContext";
import { loyaltyKeyParam, rememberLoyaltyKey, forgetLoyaltyKey } from "@/lib/loyaltyKey";

const STORAGE_KEY = "bk_loyalty_phone";

function fmtPhone(digits) {
  const d = (digits || "").replace(/\D/g, "").slice(0, 10);
  if (d.length < 4) return d;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

export default function RewardsPage() {
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [savedPhone, setSavedPhone] = useState(
    () => localStorage.getItem(STORAGE_KEY) || ""
  );
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [signingUp, setSigningUp] = useState(false);
  const { settings } = useSiteSettings();
  const { user, login, checkAuth } = useAuth();

  const digits = phone.replace(/\D/g, "");

  useEffect(() => {
    if (!savedPhone) return;
    setLoading(true);
    api
      .get(`/loyalty/${savedPhone}${loyaltyKeyParam()}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [savedPhone]);

  const lookup = async () => {
    if (digits.length < 7) {
      toast.error("Enter a valid phone number");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get(`/loyalty/${digits}${loyaltyKeyParam()}`);
      setData(data);
      if (data.exists) {
        localStorage.setItem(STORAGE_KEY, digits);
        setSavedPhone(digits);
      }
    } catch {
      toast.error("Lookup failed — try again");
    } finally {
      setLoading(false);
    }
  };

  const signup = async () => {
    const num = digits.length >= 7 ? digits : savedPhone;
    if (!num || num.length < 7) {
      toast.error("Enter a valid phone number");
      return;
    }
    setSigningUp(true);
    try {
      // Signed in with Google → link the phone to the account so it's
      // remembered securely across devices.
      if (user) {
        const { data } = await authApi.post("/auth/link-loyalty", {
          phone: num,
          name: name.trim() || null,
        });
        rememberLoyaltyKey({ access_key: data.access_key });
        localStorage.setItem(STORAGE_KEY, num);
        setSavedPhone(num);
        checkAuth?.();
        toast.success("Rewards linked to your account!");
        return;
      }
      const { data } = await api.post("/loyalty/signup", {
        phone: num,
        name: name.trim() || null,
      });
      rememberLoyaltyKey(data);
      setData(data);
      localStorage.setItem(STORAGE_KEY, num);
      setSavedPhone(num);
      if (data.unlocked) toast.success(data.exists && savedPhone ? "Unlocked — welcome back!" : "You're in! Start ordering to earn points.");
      else toast.error("That name doesn't match what's on this account — try again.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign-up failed");
    } finally {
      setSigningUp(false);
    }
  };

  const signOut = () => {
    localStorage.removeItem(STORAGE_KEY);
    forgetLoyaltyKey();
    setSavedPhone("");
    setData(null);
    setPhone("");
    setName("");
  };

  const dismissCredits = async () => {
    setData((d) => ({ ...d, unread_credits: [] }));
    try {
      await api.post(`/loyalty/${data.phone}/credits-seen`);
    } catch {
      /* best effort — notice will simply show again next visit */
    }
  };

  // Fetch tiers dynamically from backend so admin-added rewards show up here.
  const [tiers, setTiers] = useState([]);
  useEffect(() => {
    api.get("/rewards")
      .then((r) => setTiers(r.data))
      .catch(() => setTiers([]));
  }, []);

  const TIER_COLORS = [
    "bg-emerald-500",
    "bg-brand-yellow",
    "bg-amber-500",
    "bg-brand-red",
    "bg-orange-600",
    "bg-sky-500",
    "bg-fuchsia-500",
    "bg-lime-500",
  ];

  return (
    <div className="min-h-[100dvh] bg-stone-100 flex justify-center overflow-x-clip">
      <div className="w-full max-w-md lg:max-w-2xl min-h-[100dvh] bg-[#FDFBF7] phone-shell rustic-paper overflow-x-clip">
        <div className="chalkboard-band px-4 py-3 flex items-center gap-2 border-b-4 border-brand-red">
          <Link
            to="/"
            className="p-1 rounded-full hover:bg-white/10"
            data-testid="rewards-back-link"
          >
            <ChevronLeft className="w-5 h-5 text-brand-yellow" />
          </Link>
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-brand-yellow" />
            <div className="font-display text-brand-yellow text-xl">
              <T k="rewards.title" d="Rewards" />
            </div>
          </div>
        </div>

        <div className="p-5 space-y-5">
          {/* Google sign-in banner */}
          {user ? (
            <div
              className="flex items-center gap-3 bg-white border border-stone-200 rounded-xl p-3"
              data-testid="rewards-google-account"
            >
              {user.picture ? (
                <img src={user.picture} alt="" referrerPolicy="no-referrer" className="w-9 h-9 rounded-full object-cover border border-stone-200" />
              ) : (
                <div className="w-9 h-9 rounded-full bg-brand-yellow border-2 border-stone-900 flex items-center justify-center font-black text-stone-900">
                  {(user.name || user.email || "?").charAt(0).toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-stone-900 truncate">{user.name || "Signed in with Google"}</div>
                <div className="text-xs text-stone-500 truncate">
                  {savedPhone ? "Rewards linked ✓" : "Link your phone below to save your rewards"}
                </div>
              </div>
            </div>
          ) : (
            <button
              onClick={login}
              data-testid="rewards-google-signin-btn"
              className="w-full flex items-center justify-center gap-2 bg-stone-900 text-brand-yellow py-2.5 rounded-full text-xs uppercase tracking-widest font-black hover:brightness-125 active:scale-[0.98] transition-all"
            >
              <UserRound className="w-4 h-4" /> Sign in with Google
            </button>
          )}

          {!savedPhone && !data ? (
            // Signup / Lookup view
            <>
              <div className="text-center">
                <div className="w-16 h-16 mx-auto rounded-full bg-brand-red flex items-center justify-center mb-3">
                  <Star className="w-8 h-8 text-brand-yellow" />
                </div>
                <div className="font-display text-3xl text-brand-red leading-tight">
                  {settings.rewards_signup_headline}
                </div>
                <p className="text-sm text-stone-600 mt-2 leading-relaxed">
                  {settings.rewards_signup_tagline}
                  <br />
                  <span className="text-[13px]">
                    50 = Drink · 150 = Wrap · 200 = Chicken Sandwich · 250 = Burger · 400 = Brisket Fries
                  </span>
                </p>
              </div>

              <div className="space-y-3">
                <label className="block">
                  <span className="text-[10px] uppercase tracking-widest text-stone-500 font-bold flex items-center gap-1">
                    <Phone className="w-3 h-3" /> <T k="rewards.phone_label" d="Phone number" />
                  </span>
                  <input
                    type="tel"
                    inputMode="tel"
                    value={fmtPhone(phone)}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                    placeholder="4795551234 or (479) 555-1234"
                    data-testid="rewards-phone-input"
                    className="mt-1 w-full px-4 py-3 rounded-lg border-2 border-stone-300 bg-white outline-none focus:border-brand-red text-base font-mono tracking-wide"
                  />
                </label>

                <label className="block">
                  <span className="text-[10px] uppercase tracking-widest text-stone-500 font-bold flex items-center gap-1">
                    <UserRound className="w-3 h-3" /> First name (optional)
                  </span>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Jane"
                    data-testid="rewards-name-input"
                    className="mt-1 w-full px-4 py-2.5 rounded-lg border border-stone-300 bg-white outline-none focus:border-brand-red text-sm"
                  />
                </label>

                <button
                  onClick={signup}
                  disabled={digits.length < 7 || signingUp}
                  data-testid="rewards-signup-btn"
                  className="w-full bg-brand-red text-white py-3 rounded-full font-black uppercase tracking-widest text-sm hover:bg-red-800 disabled:opacity-50 active:scale-[0.98] transition-all"
                >
                  {signingUp ? "Signing in..." : <T k="rewards.signup_btn" d="Sign In / Join Free" />}
                </button>
              </div>

              <div className="text-[11px] text-stone-400 text-center leading-relaxed pt-2">
                <T
                  k="rewards.footnote"
                  d="No email, no password. Just your phone number — same one you use at checkout. Existing member? Just enter your number and tap Sign In."
                />
              </div>
            </>
          ) : loading || !data ? (
            <div className="font-hand text-3xl text-stone-400 text-center py-16">
              <T k="rewards.loading" d="Loading your rewards..." />
            </div>
          ) : (
            // Member view
            <>
              <div className="text-center">
                <div className="text-[11px] uppercase tracking-widest text-stone-500">
                  {data.name ? `Hi ${data.name}!` : <T k="rewards.welcome_back" d="Welcome back" />}
                </div>
                <div
                  className="font-display text-brand-red text-6xl mt-1 leading-none"
                  data-testid="rewards-balance-display"
                >
                  {data.points}
                </div>
                <div className="text-xs uppercase tracking-widest text-stone-600 font-bold">
                  <T k="rewards.points_label" d="Points" />
                </div>
              </div>

              {/* "We made it right" notice for admin-credited points */}
              {data.unread_credits?.length > 0 && (
                <div
                  className="rounded-2xl border-2 border-brand-yellow bg-brand-yellow/15 p-4 space-y-2 animate-float-in"
                  data-testid="rewards-credit-notice"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-brand-yellow flex items-center justify-center shrink-0">
                      <Gift className="w-5 h-5 text-stone-900" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display text-brand-red text-xl leading-tight">
                        <T k="rewards.credit_title" d="A little something from us" />
                      </div>
                      {data.unread_credits.map((c) => (
                        <div key={c.id} className="text-sm text-stone-800 mt-1" data-testid={`rewards-credit-${c.id}`}>
                          <span className="font-black text-emerald-700">+{c.points} points</span>
                          {c.customer_note ? ` — “${c.customer_note}”` : ""}
                          <span className="text-stone-400 text-xs ml-1">
                            {new Date(c.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <button
                    onClick={dismissCredits}
                    data-testid="rewards-credit-dismiss-btn"
                    className="w-full py-2 rounded-full bg-stone-900 text-white text-[11px] uppercase tracking-widest font-bold"
                  >
                    <T k="rewards.credit_dismiss" d="Thanks, got it!" />
                  </button>
                </div>
              )}

              {/* Reward tiers */}
              {tiers.map((tier, idx) => {
                const unlocked = data.points >= tier.points;
                const pct = Math.min(100, (data.points / tier.points) * 100);
                const color = TIER_COLORS[idx % TIER_COLORS.length];
                const sub = tier.fixed_item
                  ? `${tier.points} points · ${tier.fixed_item.toLowerCase()}`
                  : `${tier.points} points · pick any ${(tier.menu_category || "").toLowerCase().replace(/s$/, "")}`;
                return (
                  <div
                    key={tier.key}
                    data-testid={`rewards-tier-${tier.key}`}
                    className="bg-white border border-stone-200 rounded-xl p-4 space-y-2"
                  >
                    <div className="flex items-center gap-2">
                      <Gift className="w-5 h-5 text-brand-red shrink-0" />
                      <div className="flex-1">
                        <div className="font-bold text-stone-800">{tier.label}</div>
                        <div className="text-[11px] text-stone-500">{sub}</div>
                      </div>
                      {unlocked ? (
                        <span
                          className="text-[10px] uppercase tracking-widest bg-emerald-600 text-white font-black px-2.5 py-1 rounded-full"
                          data-testid={`rewards-${tier.key}-ready`}
                        >
                          <T k="rewards.ready" d="Ready!" />
                        </span>
                      ) : (
                        <span className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">
                          {tier.points - data.points} to go
                        </span>
                      )}
                    </div>
                    <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${color} transition-all`}
                        style={{ width: `${pct}%` }}
                        data-testid={`rewards-${tier.key}-progress`}
                      />
                    </div>
                  </div>
                );
              })}

              {/* Lifetime stats — only once this device is verified */}
              {data.unlocked ? (
                <div className="bg-brand-yellow/20 border border-brand-yellow/50 rounded-xl p-4 grid grid-cols-2 gap-4 text-center">
                  <div>
                    <div className="font-display text-2xl text-stone-900">
                      {data.lifetime_earned}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-stone-600 font-bold">
                      Lifetime Earned
                    </div>
                  </div>
                  <div>
                    <div className="font-display text-2xl text-stone-900">
                      {data.lifetime_redeemed}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-stone-600 font-bold">
                      <T k="rewards.lifetime_redeemed" d="Lifetime Redeemed" />
                    </div>
                  </div>
                </div>
              ) : (
                <form
                  onSubmit={(e) => { e.preventDefault(); signup(); }}
                  className="bg-white border border-stone-200 rounded-xl p-4 space-y-2"
                  data-testid="rewards-unlock-card"
                >
                  <div className="text-sm font-bold text-stone-800">
                    <T k="rewards.unlock_title" d="New device? Confirm it's you" />
                  </div>
                  <div className="text-xs text-stone-500">
                    <T k="rewards.unlock_body" d="Enter the name you signed up with to see your name and lifetime history on this phone." />
                  </div>
                  <div className="flex gap-2">
                    <input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Your name"
                      data-testid="rewards-unlock-name-input"
                      className="flex-1 px-3 py-2 rounded-lg border-2 border-stone-200 text-sm outline-none focus:border-brand-red"
                    />
                    <button
                      type="submit"
                      disabled={signingUp || !name.trim()}
                      data-testid="rewards-unlock-btn"
                      className="px-4 py-2 rounded-lg bg-stone-900 text-white text-xs font-bold uppercase tracking-widest disabled:opacity-40"
                    >
                      Unlock
                    </button>
                  </div>
                </form>
              )}

              <Link
                to="/"
                data-testid="rewards-order-cta"
                className="w-full flex items-center justify-center gap-2 bg-brand-red text-white py-3 rounded-full font-black uppercase tracking-widest text-sm hover:bg-red-800 active:scale-[0.98] transition-all"
              >
                <Sparkles className="w-4 h-4" />
                <T k="rewards.order_cta" d="Order to Earn More" />
              </Link>

              <button
                onClick={signOut}
                data-testid="rewards-signout-btn"
                className="w-full flex items-center justify-center gap-1.5 text-xs text-stone-500 hover:text-stone-800 py-2"
              >
                <LogOut className="w-3.5 h-3.5" />
                Sign out ({fmtPhone(data.phone)})
              </button>

              <div className="text-[11px] text-stone-400 text-center leading-relaxed">
                <T k="rewards.redeem_note" d="Redeem your points on the checkout screen when you next order." />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
